"use client"

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { getAuthHeader } from '../utils/auth';
import { useOutletContext } from 'react-router-dom';
import { isEnglishOperatorMode } from '../utils/operatorMode';

const API_URL = window.location.hostname === 'localhost' 
  ? (process.env.REACT_APP_API_URL || "http://localhost:3173") 
  : window.location.origin;
const PUBLISH_DOMAIN = "https://sb-trk.site";

const LP_COPY = {
    ja: {
        pageTitle: 'LPホスティング',
        pageSubtitle: 'Conversion-Ready Landing Pages',
        createButton: '新規LP作成',
        freeLimit: '無料プランでは公開できるLPは1つまでです。既存のLPを削除するか、プランのアップグレードをご検討ください。',
        loading: 'Assembling Assets...',
        emptyTitle: 'LPがまだありません',
        emptyBody: 'AIで生成したHTMLを貼り付けて、即座にコンバージョンエンジンを起動しましょう。',
        emptyCta: 'Deploy First Landing Page →',
        deleteConfirm: 'このLPを削除しますか？',
        deleteFailed: '削除失敗',
        dataError: 'データ取得エラー',
        copyUrl: 'URLをコピーしました',
        views: 'Views',
        aiMenu: {
            mainAction: 'Main Action',
            generatePrompt: 'AI指示文を生成',
            generatePromptSub: 'Generate LP Prompt',
            production: 'Production (制作)',
            geminiProduction: 'Gemini 制作専用',
            chatgptProduction: 'ChatGPT 制作専用',
            inspection: 'Inspection (検品)',
            geminiInspection: 'Gemini 検品専用',
            chatgptInspection: 'ChatGPT 検品専用'
        },
        prompt: {
            title: 'LP作成アシスト',
            subtitle: 'Headless LP Engine',
            note: '連携フォームの選択は不要です。AIが作成したフォームをシステムが自動で「乗っ取って」計測・自動返信を開始します。',
            serviceName: 'サービス・商品名 *',
            servicePlaceholder: '例: 自社サービス名',
            purpose: 'LPの目的 *',
            purposePlaceholder: '例: 期間限定の資料請求',
            target: 'ターゲット',
            targetPlaceholder: '例: IT企業のマネージャー',
            appeal: '訴求ポイント',
            appealPlaceholder: '・導入コスト0円\n・成約率が30%向上',
            tone: 'デザインの雰囲気',
            tones: {
                professional: '信頼感・誠実',
                friendly: '親しみやすい・ポップ',
                urgent: 'LPらしい緊急性'
            },
            generate: '指示文を生成する',
            copied: '✅ コピーしました！',
            copyPrompt: '指示文をコピーする',
            retry: '← やり直す'
        },
        editor: {
            editTitle: 'LPを編集',
            newTitle: '新規LP作成',
            environment: 'Managed Hosting Environment',
            tabs: { basic: 'basic', detail: 'detail', images: 'images' },
            title: '管理名称',
            slug: '公開URLスラッグ',
            linkedForm: '連携フォーム',
            unselected: '-- 未選択 --',
            online: 'このLPをオンラインにする',
            headScripts: '外部スクリプト (Head)',
            ogp: 'OGP / SNSシェア設定',
            ogTitle: 'OGPタイトル',
            ogDescription: 'OGP説明文',
            uploadImage: '画像をアップロード',
            uploadingImage: 'アップロード中...',
            uploadFirst: '画像をアップロードするには、先にLPを一度保存してください。',
            filenameCopied: 'ファイル名をコピーしました',
            editorMode: 'コード編集',
            visualMode: '見ながら編集',
            previewMode: 'プレビュー',
            desktopPreview: 'PC',
            mobilePreview: 'スマホ',
            noElementSelected: 'プレビュー内の文字または画像をクリックしてください。',
            selectedText: '選択中の文字',
            applyText: '文字を反映',
            commonBreak: '共通改行',
            desktopBreak: 'PCだけ改行',
            mobileBreak: 'スマホだけ改行',
            selectedImage: '選択中の画像',
            replaceSelectedImage: 'この画像に差し替え',
            selectImageFirst: '先にプレビュー内の画像をクリックしてください。',
            allAssetsLinked: 'All Assets Linked',
            missingImages: 'Missing Images',
            bodyHtml: 'Body HTML Content',
            codeNote: 'CSSはHTML内の<style>へ、必要なJSはHTML内の<script>へ入れてください。LPホスティング側で別々のCSS/JS欄は管理しません。',
            discard: 'Discard Changes',
            save: '設定を保存して公開を更新',
            initialHtml: '<!DOCTYPE html>\n<html lang="ja">\n<head>\n    <meta charset="UTF-8">\n    <title>LP</title>\n</head>\n<body>\n    <h1>Welcome to LP</h1>\n    <div id="sb-form-placeholder">{{form}}</div>\n</body>\n</html>'
        }
    },
    en: {
        pageTitle: 'Landing Pages',
        pageSubtitle: 'Conversion-Ready Landing Pages',
        createButton: 'Create Landing Page',
        freeLimit: 'Free plans can publish one landing page only. Delete an existing page or upgrade your plan.',
        loading: 'Assembling Assets...',
        emptyTitle: 'No landing pages yet',
        emptyBody: 'Paste your HTML, connect a form, and publish a conversion-ready landing page.',
        emptyCta: 'Deploy First Landing Page →',
        deleteConfirm: 'Delete this landing page?',
        deleteFailed: 'Delete failed',
        dataError: 'Could not load landing page data.',
        copyUrl: 'URL copied.',
        views: 'Views',
        aiMenu: {
            mainAction: 'Main Action',
            generatePrompt: 'Generate AI Prompt',
            generatePromptSub: 'Generate LP Prompt',
            production: 'Production',
            geminiProduction: 'Gemini Production',
            chatgptProduction: 'ChatGPT Production',
            inspection: 'Inspection',
            geminiInspection: 'Gemini QA',
            chatgptInspection: 'ChatGPT QA'
        },
        prompt: {
            title: 'Landing Page Assist',
            subtitle: 'Headless LP Engine',
            note: 'You do not need to select a connected form here. SalesBreaker will detect the AI-created form, capture submissions, and trigger tracking plus auto-replies.',
            serviceName: 'Service / Product Name *',
            servicePlaceholder: 'Example: Japan GTM Workspace',
            purpose: 'Landing Page Goal *',
            purposePlaceholder: 'Example: Request a demo or download materials',
            target: 'Target Audience',
            targetPlaceholder: 'Example: US SaaS founders entering Japan',
            appeal: 'Key Value Points',
            appealPlaceholder: '- Launch Japan outreach faster\n- AI-assisted research and follow-up\n- Built for B2B sales teams',
            tone: 'Design Tone',
            tones: {
                professional: 'Trustworthy / Professional',
                friendly: 'Friendly / Approachable',
                urgent: 'Direct / Sales-oriented'
            },
            generate: 'Generate Prompt',
            copied: 'Copied.',
            copyPrompt: 'Copy Prompt',
            retry: 'Start Over'
        },
        editor: {
            editTitle: 'Edit Landing Page',
            newTitle: 'Create Landing Page',
            environment: 'Managed Hosting Environment',
            tabs: { basic: 'basic', detail: 'detail', images: 'images' },
            title: 'Internal Name',
            slug: 'Public URL Slug',
            linkedForm: 'Connected Form',
            unselected: '-- Not selected --',
            online: 'Publish this landing page',
            headScripts: 'External Scripts (Head)',
            ogp: 'OGP / Social Share Settings',
            ogTitle: 'OGP Title',
            ogDescription: 'OGP Description',
            uploadImage: 'Upload Image',
            uploadingImage: 'Uploading...',
            uploadFirst: 'Save this landing page once before uploading images.',
            filenameCopied: 'Filename copied.',
            editorMode: 'Code',
            visualMode: 'Visual Edit',
            previewMode: 'Preview',
            desktopPreview: 'Desktop',
            mobilePreview: 'Mobile',
            noElementSelected: 'Click text or an image in the preview.',
            selectedText: 'Selected Text',
            applyText: 'Apply Text',
            commonBreak: 'Common Break',
            desktopBreak: 'Desktop Break',
            mobileBreak: 'Mobile Break',
            selectedImage: 'Selected Image',
            replaceSelectedImage: 'Replace Selected Image',
            selectImageFirst: 'Select an image in the preview first.',
            allAssetsLinked: 'All Assets Linked',
            missingImages: 'Missing Images',
            bodyHtml: 'Body HTML Content',
            codeNote: 'Put CSS inside a <style> tag and JavaScript inside a <script> tag in the HTML. This hosting surface does not manage separate CSS or JS files.',
            discard: 'Discard Changes',
            save: 'Save & Publish',
            initialHtml: '<!DOCTYPE html>\n<html lang="en">\n<head>\n    <meta charset="UTF-8">\n    <title>Landing Page</title>\n</head>\n<body>\n    <h1>Welcome</h1>\n    <form>\n        <input name="company" placeholder="Company" />\n        <input name="name" placeholder="Name" />\n        <input name="email" type="email" placeholder="Email" />\n        <textarea name="message" placeholder="Message"></textarea>\n        <button type="submit">Submit</button>\n    </form>\n</body>\n</html>'
        }
    }
};

const getLpCopy = (isEnglishBeta = false) => isEnglishBeta ? LP_COPY.en : LP_COPY.ja;

const LP_HELPER_STYLE_ID = 'sb-lp-helper-styles';
const LP_HELPER_STYLE = `
<style id="${LP_HELPER_STYLE_ID}">
@media (min-width: 768px) {
  .sb-break-desktop { display: block !important; }
  .sb-break-mobile { display: none !important; }
}
@media (max-width: 767px) {
  .sb-break-desktop { display: none !important; }
  .sb-break-mobile { display: block !important; }
}
</style>`;

const LP_PREVIEW_STYLE = `
<style id="sb-lp-preview-styles">
[data-sb-editable-hover="true"] { outline: 2px dashed rgba(37, 99, 235, 0.45) !important; outline-offset: 3px !important; cursor: pointer !important; }
[data-sb-selected="true"] { outline: 3px solid rgba(37, 99, 235, 0.85) !important; outline-offset: 4px !important; }
</style>`;
const LP_EDITABLE_SELECTOR = 'img,h1,h2,h3,h4,h5,h6,p,span,a,button,label,li,blockquote,figcaption,small,strong,em,div';

const injectIntoHead = (html, markup) => {
    if (!html) return markup;
    if (/<\/head>/i.test(html)) return html.replace(/<\/head>/i, `${markup}\n</head>`);
    return `${markup}\n${html}`;
};

const ensureLpHelperStyles = (html = '') => {
    const withoutExisting = html.replace(new RegExp(`<style\\b[^>]*id=["']${LP_HELPER_STYLE_ID}["'][\\s\\S]*?<\\/style>`, 'gi'), '').trim();
    return injectIntoHead(withoutExisting, LP_HELPER_STYLE);
};

const decorateEditableElements = (doc) => {
    let index = 0;
    doc.body?.querySelectorAll(LP_EDITABLE_SELECTOR).forEach(element => {
        if (['SCRIPT', 'STYLE', 'HTML', 'BODY'].includes(element.tagName)) return;
        element.setAttribute('data-sb-edit-id', `sb-edit-${index}`);
        index += 1;
    });
};

const stripEditorAttributes = (doc) => {
    doc.querySelectorAll('[data-sb-edit-id],[data-sb-editable-hover],[data-sb-selected]').forEach(element => {
        element.removeAttribute('data-sb-edit-id');
        element.removeAttribute('data-sb-editable-hover');
        element.removeAttribute('data-sb-selected');
    });
};

const serializeHtmlDocument = (doc) => `<!DOCTYPE html>\n${doc.documentElement.outerHTML}`;

// --- Icons ---
const Icons = {
    Plus: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>,
    Edit: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
    Trash: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    External: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>,
    Eye: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
    Code: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="16 18 22 12 16 6"></polyline><polyline points="8 6 2 12 8 18"></polyline></svg>,
    Copy: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path></svg>,
    Magic: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/></svg>,
    Check: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>,
    Alert: () => <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
};

// --- LP Assistant Modal (究極の自動化・Headless版) ---
function LpPromptGeneratorModal({ onClose, isEnglishBeta = false }) {
    const copy = getLpCopy(isEnglishBeta).prompt;
    const [config, setConfig] = useState({
        serviceName: "",
        theme: "",
        target: "",
        appeal: "",
        tone: "professional"
    });
    const [prompt, setPrompt] = useState("");
    const [isCopied, setIsCopied] = useState(false);

    const generatePrompt = () => {
        // AIへの指示：標準的なHTMLフォームを、特定の命名規則で作らせる
        const text = isEnglishBeta ? `
You are a world-class web designer and conversion copywriter.
Create complete HTML for a B2B landing page that maximizes qualified conversions.
Return only the HTML code. Do not include commentary, explanations, or markdown fences.

# Page Requirements
- Service / product name: ${config.serviceName}
- Landing page goal: ${config.theme}
- Target audience: ${config.target}
- Key value points: ${config.appeal}
- Design tone: ${config.tone === 'professional' ? 'trustworthy, sincere, professional' : config.tone === 'friendly' ? 'friendly, warm, approachable' : 'direct, urgent, sales-oriented'}

# Critical Form Requirements
Include a contact form on the page.
The input name attributes must exactly match these values so SalesBreaker can capture submissions:
- Company: name="company"
- Contact name: name="name"
- Email address: name="email"
- Phone number: name="phone"
- Message: name="message"

The submit button must be <button type="submit"> or <input type="submit">.
Do not write JavaScript for submission handling such as reportToSalesBreaker. SalesBreaker will detect and process the form automatically.

# Technical Requirements
1. Complete HTML: output everything from <!DOCTYPE html> through </html>.
2. CSS: write CSS in a <style> tag inside <head>. Do not use external CSS files.
3. Tracking tags: do not include Meta Pixel or tracking tags. SalesBreaker injects them automatically.
4. Images: use paths like img/filename.png.

# Output
HTML code only.
`.trim() : `
あなたは世界最高峰のWebデザイナー兼コピーライターです。
BtoB向けの、成約率(CVR)を最大化するランディングページ(LP)のHTMLコードを作成してください。
指示以外の余計な解説文（「承知しました」など）は一切不要です。

# 制作要件
- **サービス名**: ${config.serviceName}
- **LPの目的**: ${config.theme}
- **ターゲット**: ${config.target}
- **訴求ポイント**: ${config.appeal}
- **デザインの雰囲気**: ${config.tone === 'professional' ? '信頼感、誠実、プロフェッショナル' : config.tone === 'friendly' ? '親しみやすい、柔らかい' : '緊急性、限定感、セールスライティング重視'}

# 【最重要】お問い合わせフォームの仕様
ページ内に必ずお問い合わせフォームを設置してください。
各入力項目の 'name' 属性は、システム連携のため以下の名称を「完全に一致」させてください。
- 会社名: name="company"
- 担当者氏名: name="name"
- メールアドレス: name="email"
- 電話番号: name="phone"
- お問い合わせ内容: name="message"

※ 送信ボタンは必ず <button type="submit"> または <input type="submit"> で作成してください。
※ 送信時のJavaScript（reportToSalesBreaker等）は書かないでください。システムが自動で検知して処理します。

# 技術的な必須要件
1. **完全なHTML**: <!DOCTYPE html> から </html> まで一括で出力すること。
2. **CSS**: 外部ファイルは使わず、<head> 内の <style> に記述すること。
3. **計測タグ**: Meta Pixelや計測タグはシステムが自動注入するため、AI側では絶対に記述しないでください。
4. **画像**: パスは必ず img/ファイル名.png の形式にしてください。

# 出力
- HTMLコードのみを出力してください。
`.trim();
        setPrompt(text);
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(prompt);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
    };

    return (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[70] p-3 sm:p-4 backdrop-blur-sm">
            <div className="bg-white w-full max-w-xl rounded-3xl sm:rounded-[2.5rem] shadow-2xl flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
                
                {/* Header */}
                <div className="flex justify-between items-start gap-4 p-5 sm:p-8 bg-indigo-600 text-white">
                    <div className="min-w-0">
                        <h3 className="font-black text-lg sm:text-xl flex items-center gap-3"><Icons.Magic/> {copy.title}</h3>
                        <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-70 mt-1">{copy.subtitle}</p>
                    </div>
                    <button onClick={onClose} className="shrink-0 text-white/60 hover:text-white text-4xl leading-none transition-colors">&times;</button>
                </div>

                <div className="p-5 sm:p-10 overflow-y-auto max-h-[75vh] scrollbar-hide">
                    {!prompt ? (
                        <div className="space-y-5">
                            <div className="bg-indigo-50 p-5 rounded-2xl border border-indigo-100 text-indigo-900 text-[11px] font-bold leading-relaxed">
                                {copy.note}
                            </div>
                            
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 tracking-wider">{copy.serviceName}</label>
                                <input type="text" className="w-full p-4 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-indigo-50 outline-none font-bold" value={config.serviceName} onChange={e => setConfig({...config, serviceName: e.target.value})} placeholder={copy.servicePlaceholder} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 tracking-wider">{copy.purpose}</label>
                                <input type="text" className="w-full p-4 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-indigo-50 outline-none" value={config.theme} onChange={e => setConfig({...config, theme: e.target.value})} placeholder={copy.purposePlaceholder} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 tracking-wider">{copy.target}</label>
                                <input type="text" className="w-full p-4 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-indigo-50 outline-none" value={config.target} onChange={e => setConfig({...config, target: e.target.value})} placeholder={copy.targetPlaceholder} />
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 tracking-wider">{copy.appeal}</label>
                                <textarea className="w-full p-4 border border-gray-100 rounded-2xl h-28 text-sm" value={config.appeal} onChange={e => setConfig({...config, appeal: e.target.value})} placeholder={copy.appealPlaceholder}></textarea>
                            </div>
                            <div>
                                <label className="block text-[10px] font-black text-gray-400 uppercase mb-1.5 tracking-wider">{copy.tone}</label>
                                <select className="w-full p-4 border border-gray-100 rounded-2xl bg-white outline-none font-bold" value={config.tone} onChange={e => setConfig({...config, tone: e.target.value})}>
                                    <option value="professional">{copy.tones.professional}</option>
                                    <option value="friendly">{copy.tones.friendly}</option>
                                    <option value="urgent">{copy.tones.urgent}</option>
                                </select>
                            </div>

                            <button 
                                onClick={generatePrompt} 
                                disabled={!config.serviceName || !config.theme} 
                                className="w-full bg-indigo-600 text-white font-black py-5 rounded-full shadow-2xl hover:bg-indigo-700 active:scale-95 disabled:bg-gray-100 disabled:text-gray-300 mt-4"
                            >
                                {copy.generate}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-6 animate-in fade-in duration-500">
                            <textarea readOnly className="w-full h-72 sm:h-80 p-4 sm:p-6 bg-gray-900 text-indigo-300 font-mono text-xs rounded-3xl sm:rounded-[2rem] leading-relaxed" value={prompt}></textarea>
                            <div className="space-y-4">
                                <button onClick={handleCopy} className="w-full py-5 bg-indigo-600 text-white rounded-full font-black text-sm shadow-xl hover:bg-indigo-700 active:scale-95">
                                    {isCopied ? copy.copied : copy.copyPrompt}
                                </button>
                                {!isEnglishBeta && <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <a href="https://gemini.google.com/gem/1l3jQkeEtG8CXvOl16PyJO2lOS9o_Npr2?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 py-4 bg-white border-2 border-blue-100 text-blue-600 rounded-3xl hover:bg-blue-50 transition-all">
                                        <span className="text-[10px] font-black uppercase">Option A</span>
                                        <span className="text-xs font-bold">GO TO GEMINI</span>
                                    </a>
                                    <a href="https://chatgpt.com/g/g-69817bf04fb48191b82c9a12044bbfe9-sales-breaker-lpzhi-zuo-asisutanto" target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 py-4 bg-white border-2 border-green-100 text-green-600 rounded-3xl hover:bg-green-50 transition-all">
                                        <span className="text-[10px] font-black uppercase">Option B</span>
                                        <span className="text-xs font-bold">GO TO CHATGPT</span>
                                    </a>
                                </div>}
                                <button onClick={() => setPrompt("")} className="w-full py-2 text-gray-400 font-black text-[10px] uppercase tracking-[0.2em] hover:text-gray-600">{copy.retry}</button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

// --- LP Editor Modal ---
function LpEditorModal({ lp, forms, onClose, onSave, clientId, isEnglishBeta = false }) {
    const copy = getLpCopy(isEnglishBeta).editor;
    const [formData, setFormData] = useState({
        title: '',
        slug: '',
        html_content: '',
        css_content: '',
        js_content: '',
        head_tags: '',
        linked_form_id: '',
        is_active: true,
        og_title: '',
        og_description: '',
        og_image_id: '',
        publish_start_at: '',
        publish_end_at: '',
        redirect_url: '',
        footer_company_name: '',
        footer_address: '',
        footer_contact: ''
    });
    const [viewMode, setViewMode] = useState('code');
    const [images, setImages] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [htmlWarnings, setHtmlWarnings] = useState([]);
    const [activeTab, setActiveTab] = useState('basic');
    const [previewDevice, setPreviewDevice] = useState('desktop');
    const [selectedElement, setSelectedElement] = useState(null);
    const [selectedHtml, setSelectedHtml] = useState('');
    const previewFrameRef = useRef(null);
    const selectedHtmlRef = useRef(null);

    const fetchImages = useCallback(async (id) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/lps/${id}/images?client_id=${clientId}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) setImages(data.images);
        } catch (e) { console.error(e); }
    }, [clientId]);

    useEffect(() => {
        if (lp && lp.id) {
            setFormData({
                ...lp,
                is_active: lp.is_active !== 0,
                publish_start_at: lp.publish_start_at ? new Date(lp.publish_start_at).toISOString().slice(0, 16) : '',
                publish_end_at: lp.publish_end_at ? new Date(lp.publish_end_at).toISOString().slice(0, 16) : ''
            });
            fetchImages(lp.id);
        } else {
            setFormData(prev => ({
                ...prev,
                slug: `lp-${Date.now().toString(36).slice(-6)}`,
                html_content: copy.initialHtml
            }));
        }
    }, [lp, copy.initialHtml, fetchImages]);

    const validateHtml = useCallback(() => {
        const warnings = [];
        const html = formData.html_content;
        const imgRegex = /src=["'](?!http|data:)([^"']+)["']|url\(['"]?(?!http|data:)([^'")]+)['"]?\)/g;
        let match;
        const usedFiles = new Set();
        while ((match = imgRegex.exec(html)) !== null) {
            const path = match[1] || match[2];
            if (path) usedFiles.add(path.split('/').pop());
        }
        usedFiles.forEach(f => {
            if (!images.some(img => img.filename === f)) warnings.push(f);
        });
        setHtmlWarnings(warnings);
        return warnings.length === 0;
    }, [formData.html_content, images]);

    useEffect(() => { validateHtml(); }, [validateHtml]);

    const handleUpload = async (e) => {
        if (!lp?.id) return alert(copy.uploadFirst);
        const file = e.target.files[0];
        if (!file) return;
        setIsUploading(true);
        const up = new FormData();
        up.append('file', file);
        up.append('client_id', clientId);
        try {
            const res = await fetch(`${API_URL}/api/marketing/lps/${lp.id}/images`, { method: 'POST', headers: getAuthHeader(), body: up });
            if (res.ok) fetchImages(lp.id);
        } finally { setIsUploading(false); }
    };

    const getImageUrl = useCallback((filename) => {
        const lpId = formData.id || lp?.id;
        return lpId ? `${PUBLISH_DOMAIN}/p/img/${lpId}/${filename}` : '';
    }, [formData.id, lp?.id]);

    const rewriteImagePathsForPreview = useCallback((html = '') => {
        const lpId = formData.id || lp?.id;
        if (!lpId) return html;
        let next = html.replace(/=(["'])(\.?\/?img\/)([^"']+)\1/g, (match, quote, prefix, filename) => {
            if (/^https?:\/\//i.test(filename) || filename.startsWith('/p/img/')) return match;
            return `=${quote}${PUBLISH_DOMAIN}/p/img/${lpId}/${filename}${quote}`;
        });
        next = next.replace(/url\((["']?)(\.?\/?img\/)([^'")]+)\1\)/g, (match, quote, prefix, filename) => {
            if (/^https?:\/\//i.test(filename) || filename.startsWith('/p/img/')) return match;
            return `url(${quote}${PUBLISH_DOMAIN}/p/img/${lpId}/${filename}${quote})`;
        });
        return next;
    }, [formData.id, lp?.id]);

    const getPreviewHtml = useCallback((editable = false) => {
        let h = ensureLpHelperStyles(formData.html_content || '');
        if (editable) {
            const doc = new DOMParser().parseFromString(h, 'text/html');
            decorateEditableElements(doc);
            h = serializeHtmlDocument(doc);
        }
        h = rewriteImagePathsForPreview(h);
        h = h.replace('{{form}}', '<div style="background:#f1f5f9; border:2px dashed #cbd5e1; padding:40px; text-align:center; color:#64748b; border-radius:1.5rem; font-weight:900; font-size:12px; letter-spacing:0.1em; margin:20px 0;">[ FORM INTEGRATED ]</div>');
        if (editable) h = injectIntoHead(h, LP_PREVIEW_STYLE);
        return h;
    }, [formData.html_content, rewriteImagePathsForPreview]);

    const updateHtmlElement = useCallback((editId, updater) => {
        if (!editId) return;
        const doc = new DOMParser().parseFromString(formData.html_content || '', 'text/html');
        decorateEditableElements(doc);
        const element = doc.querySelector(`[data-sb-edit-id="${editId}"]`);
        if (!element) return;
        updater(element);
        stripEditorAttributes(doc);
        setFormData(prev => ({ ...prev, html_content: serializeHtmlDocument(doc) }));
    }, [formData.html_content]);

    const applySelectedText = useCallback(() => {
        if (!selectedElement || selectedElement.type !== 'text') return;
        updateHtmlElement(selectedElement.editId, element => {
            element.innerHTML = selectedHtml;
        });
    }, [selectedElement, selectedHtml, updateHtmlElement]);

    const replaceImageByEditId = useCallback((editId, filename) => {
        if (!editId || !filename) return;
        updateHtmlElement(editId, element => {
            if (element.tagName === 'IMG') {
                element.setAttribute('src', `img/${filename}`);
            }
        });
        setSelectedElement(prev => prev ? { ...prev, src: getImageUrl(filename) } : prev);
    }, [getImageUrl, updateHtmlElement]);

    const replaceSelectedImage = useCallback((filename) => {
        if (!selectedElement || selectedElement.type !== 'image') {
            alert(copy.selectImageFirst);
            return;
        }
        replaceImageByEditId(selectedElement.editId, filename);
    }, [copy.selectImageFirst, replaceImageByEditId, selectedElement]);

    const insertBreak = useCallback((markup) => {
        const textarea = selectedHtmlRef.current;
        const start = textarea?.selectionStart ?? selectedHtml.length;
        const end = textarea?.selectionEnd ?? selectedHtml.length;
        const next = `${selectedHtml.slice(0, start)}${markup}${selectedHtml.slice(end)}`;
        setSelectedHtml(next);
        requestAnimationFrame(() => {
            if (!selectedHtmlRef.current) return;
            selectedHtmlRef.current.focus();
            selectedHtmlRef.current.setSelectionRange(start + markup.length, start + markup.length);
        });
    }, [selectedHtml]);

    const wirePreviewFrame = useCallback(() => {
        if (viewMode !== 'visual') return;
        const frame = previewFrameRef.current;
        const doc = frame?.contentDocument;
        if (!doc?.body) return;

        const clearHover = () => doc.querySelectorAll('[data-sb-editable-hover="true"]').forEach(el => el.removeAttribute('data-sb-editable-hover'));
        const clearSelected = () => doc.querySelectorAll('[data-sb-selected="true"]').forEach(el => el.removeAttribute('data-sb-selected'));
        const normalizeTarget = (rawTarget) => {
            if (!rawTarget?.closest) return null;
            const target = rawTarget.closest(LP_EDITABLE_SELECTOR);
            if (!target || target === doc.body || target === doc.documentElement) return null;
            if (['SCRIPT', 'STYLE', 'HTML', 'BODY'].includes(target.tagName)) return null;
            if (!target.getAttribute('data-sb-edit-id')) return null;
            return target;
        };

        doc.addEventListener('mouseover', (event) => {
            const target = normalizeTarget(event.target);
            if (!target || target.getAttribute('data-sb-selected') === 'true') return;
            target.setAttribute('data-sb-editable-hover', 'true');
        }, true);
        doc.addEventListener('mouseout', clearHover, true);
        doc.addEventListener('click', (event) => {
            const target = normalizeTarget(event.target);
            if (!target) return;
            event.preventDefault();
            event.stopPropagation();
            clearHover();
            clearSelected();
            target.setAttribute('data-sb-selected', 'true');
            const type = target.tagName === 'IMG' ? 'image' : 'text';
            const info = {
                type,
                tag: target.tagName.toLowerCase(),
                editId: target.getAttribute('data-sb-edit-id'),
                label: type === 'image'
                    ? (target.getAttribute('alt') || target.getAttribute('src') || 'image')
                    : (target.innerText || target.textContent || '').trim().slice(0, 120),
                src: target.getAttribute('src') || ''
            };
            setSelectedElement(info);
            setSelectedHtml(type === 'text' ? target.innerHTML : '');
            if (type === 'image') setActiveTab('images');
        }, true);
        doc.addEventListener('dragover', (event) => event.preventDefault(), true);
        doc.addEventListener('drop', (event) => {
            event.preventDefault();
            const filename = event.dataTransfer?.getData('text/plain');
            const target = event.target?.closest?.('img');
            if (!filename || !target) return;
            replaceImageByEditId(target.getAttribute('data-sb-edit-id'), filename);
        }, true);
    }, [replaceImageByEditId, viewMode]);

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-2 sm:p-4 backdrop-blur-md">
            <div className="bg-white w-full max-w-[98vw] lg:max-w-[90vw] rounded-2xl sm:rounded-[3rem] shadow-2xl h-[96vh] sm:h-[94vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300 font-sans">
                <div className="flex justify-between items-center gap-4 px-5 py-4 sm:px-10 sm:py-6 border-b bg-white">
                    <div>
                        <h3 className="font-black text-xl sm:text-2xl text-gray-900 tracking-tight">{lp?.id ? copy.editTitle : copy.newTitle}</h3>
                        <p className="text-[10px] text-gray-400 font-bold tracking-widest uppercase mt-1">{copy.environment}</p>
                    </div>
                    <button onClick={onClose} className="shrink-0 text-gray-300 hover:text-gray-600 transition-colors text-4xl sm:text-5xl leading-none">&times;</button>
                </div>

                <div className="flex-grow overflow-hidden flex flex-col md:flex-row bg-gray-50/50">
                    {/* Left: Editor Panel */}
                    <div className="w-full md:w-[28%] overflow-y-auto border-r p-4 sm:p-8 space-y-6 sm:space-y-10 scrollbar-hide">
                        <div className="flex border-b mb-4 overflow-x-auto">
                            {['basic', 'detail', 'images'].map(t => (
                                <button key={t} onClick={() => setActiveTab(t)} className={`min-w-24 flex-1 pb-4 text-[10px] font-black uppercase tracking-[0.2em] transition-all ${activeTab === t ? 'text-blue-600 border-b-4 border-blue-600' : 'text-gray-300'}`}>{copy.tabs[t]}</button>
                            ))}
                        </div>

                        {activeTab === 'basic' && (
                            <div className="space-y-6 animate-in slide-in-from-left-2 duration-300">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-2">{copy.title}</label>
                                    <input type="text" className="w-full p-4 border border-gray-100 rounded-2xl font-bold bg-white outline-none focus:ring-4 focus:ring-blue-50" value={formData.title} onChange={e => setFormData({...formData, title: e.target.value})} />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-2">{copy.slug}</label>
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="text-gray-300 font-bold">/p/</span>
                                        <input type="text" className="flex-grow p-4 border border-gray-100 rounded-2xl font-mono text-sm bg-white outline-none" value={formData.slug} onChange={e => setFormData({...formData, slug: e.target.value})} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 text-blue-600">{copy.linkedForm}</label>
                                    <select className="w-full p-4 border border-blue-100 rounded-2xl text-sm font-bold bg-blue-50/30 outline-none" value={formData.linked_form_id} onChange={e => setFormData({...formData, linked_form_id: e.target.value})}>
                                        <option value="">{copy.unselected}</option>
                                        {forms.map(f => <option key={f.id} value={f.id}>{f.title}</option>)}
                                    </select>
                                </div>
                                <label className="flex items-center gap-4 p-5 bg-white border border-gray-100 rounded-3xl cursor-pointer hover:shadow-lg transition-all group">
                                    <input type="checkbox" checked={formData.is_active} onChange={e => setFormData({...formData, is_active: e.target.checked})} className="w-6 h-6 rounded-xl text-blue-600 border-gray-200" />
                                    <span className="text-sm font-black text-gray-700 tracking-widest">{copy.online}</span>
                                </label>
                            </div>
                        )}

                        {activeTab === 'detail' && (
                            <div className="space-y-8 animate-in slide-in-from-left-2 duration-300">
                                <div>
                                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-2">{copy.headScripts}</label>
                                    <textarea className="w-full p-4 border border-gray-100 rounded-2xl font-mono text-[10px] h-24 bg-white outline-none" value={formData.head_tags} onChange={e => setFormData({...formData, head_tags: e.target.value})} placeholder="<script>...</script>"></textarea>
                                </div>
                                <div className="p-6 bg-white border border-gray-100 rounded-[2rem] space-y-4">
                                    <h4 className="text-[10px] font-black text-pink-600 uppercase tracking-widest">{copy.ogp}</h4>
                                    <input type="text" placeholder={copy.ogTitle} className="w-full p-3 border border-gray-100 rounded-xl text-xs" value={formData.og_title} onChange={e => setFormData({...formData, og_title: e.target.value})} />
                                    <textarea placeholder={copy.ogDescription} className="w-full p-3 border border-gray-100 rounded-xl text-xs h-20" value={formData.og_description} onChange={e => setFormData({...formData, og_description: e.target.value})}></textarea>
                                </div>
                            </div>
                        )}

                        {activeTab === 'images' && (
                            <div className="space-y-4 animate-in slide-in-from-left-2 duration-300">
                                <label className="block w-full border-4 border-dashed border-gray-100 rounded-3xl sm:rounded-[2.5rem] p-6 sm:p-10 text-center cursor-pointer hover:bg-white hover:border-blue-200 transition-all group">
                                    <input type="file" className="hidden" accept="image/*" onChange={handleUpload} />
                                    <div className="text-gray-300 group-hover:text-blue-500 mb-2"><Icons.Plus /></div>
                                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest group-hover:text-blue-600">{isUploading ? copy.uploadingImage : copy.uploadImage}</span>
                                </label>
                                <div className="grid grid-cols-2 gap-4">
                                    {images.map(img => (
                                        <div
                                            key={img.id}
                                            draggable
                                            onDragStart={(event) => event.dataTransfer.setData('text/plain', img.filename)}
                                            className="group relative aspect-square bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl transition-all"
                                        >
                                            <img src={getImageUrl(img.filename)} className="w-full h-full object-cover" alt="" />
                                            <div className="absolute inset-0 bg-indigo-900/80 opacity-100 transition-all flex flex-col items-center justify-center p-3 sm:opacity-0 sm:group-hover:opacity-100">
                                                <p className="text-[9px] text-white font-mono truncate w-full text-center mb-3">{img.filename}</p>
                                                <div className="flex gap-2">
                                                    <button onClick={() => replaceSelectedImage(img.filename)} className="px-3 py-2 bg-white rounded-full text-[9px] font-black text-indigo-600 shadow-2xl hover:scale-105 active:scale-95 transition-all">{copy.replaceSelectedImage}</button>
                                                    <button onClick={() => { navigator.clipboard.writeText(img.filename); alert(copy.filenameCopied); }} className="p-2.5 bg-white rounded-full text-indigo-600 shadow-2xl hover:scale-110 active:scale-95 transition-all"><Icons.Copy/></button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right: Code & Preview Panel */}
                    <div className="flex-grow flex flex-col bg-white">
                        <div className="flex flex-col gap-3 px-4 py-3 sm:px-8 sm:py-4 bg-gray-100/80 border-b xl:flex-row xl:justify-between xl:items-center">
                            <div className="grid grid-cols-3 gap-2 bg-white/50 p-1.5 rounded-2xl shadow-inner border border-white">
                                <button onClick={() => setViewMode('code')} className={`px-4 sm:px-8 py-2.5 text-[10px] font-black rounded-xl transition-all ${viewMode === 'code' ? 'bg-gray-900 text-white shadow-xl' : 'text-gray-400 hover:text-gray-600'}`}>{copy.editorMode}</button>
                                <button onClick={() => setViewMode('visual')} className={`px-4 sm:px-8 py-2.5 text-[10px] font-black rounded-xl transition-all ${viewMode === 'visual' ? 'bg-gray-900 text-white shadow-xl' : 'text-gray-400 hover:text-gray-600'}`}>{copy.visualMode}</button>
                                <button onClick={() => setViewMode('preview')} className={`px-4 sm:px-8 py-2.5 text-[10px] font-black rounded-xl transition-all ${viewMode === 'preview' ? 'bg-gray-900 text-white shadow-xl' : 'text-gray-400 hover:text-gray-600'}`}>{copy.previewMode}</button>
                            </div>
                            
                            {/* 画像リンク警告ロジック復元 */}
                            <div className="flex flex-wrap gap-3 overflow-x-auto">
                                {viewMode !== 'code' && (
                                    <div className="grid grid-cols-2 gap-1 rounded-xl border border-white bg-white/70 p-1">
                                        <button onClick={() => setPreviewDevice('desktop')} className={`rounded-lg px-4 py-2 text-[10px] font-black ${previewDevice === 'desktop' ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>{copy.desktopPreview}</button>
                                        <button onClick={() => setPreviewDevice('mobile')} className={`rounded-lg px-4 py-2 text-[10px] font-black ${previewDevice === 'mobile' ? 'bg-blue-600 text-white' : 'text-gray-400'}`}>{copy.mobilePreview}</button>
                                    </div>
                                )}
                                {htmlWarnings.length > 0 ? (
                                    <div className="bg-red-50 text-red-500 px-4 py-2 rounded-xl border border-red-100 text-[10px] font-black uppercase flex items-center gap-2 animate-pulse min-w-0">
                                        <Icons.Alert /> {copy.missingImages}: {htmlWarnings.join(', ')}
                                    </div>
                                ) : (
                                    <div className="bg-green-50 text-green-600 px-4 py-2 rounded-xl border border-green-100 text-[10px] font-black uppercase flex items-center gap-2">
                                        <Icons.Check /> {copy.allAssetsLinked}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="flex-grow overflow-hidden relative">
                            {viewMode === 'code' ? (
                                <div className="h-full flex flex-col">
                                    {/* HTML Editor */}
                                    <div className="flex-grow min-h-[40%] flex flex-col">
                                        <div className="bg-gray-800 text-[9px] text-gray-400 px-4 sm:px-6 py-2 font-black uppercase tracking-[0.2em] border-b border-gray-700">{copy.bodyHtml}</div>
                                        <textarea className="flex-grow w-full p-4 sm:p-8 bg-gray-950 text-green-400 font-mono text-xs sm:text-sm outline-none resize-none leading-relaxed scrollbar-hide" value={formData.html_content} onChange={e => setFormData({...formData, html_content: e.target.value})} spellCheck="false" />
                                    </div>
                                    <div className="border-t-4 border-gray-800 bg-gray-900 px-4 py-4 sm:px-8">
                                        <div className="rounded-2xl border border-gray-700 bg-gray-950/70 p-4 text-xs leading-6 text-gray-300">
                                            {copy.codeNote}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex h-full flex-col bg-slate-100 xl:flex-row">
                                    <div className="flex flex-1 items-start justify-center overflow-auto p-4">
                                        <div className={`${previewDevice === 'mobile' ? 'w-[390px] max-w-full min-h-full rounded-[2rem] shadow-2xl ring-8 ring-gray-900/10' : 'w-full min-h-full shadow-sm'} overflow-hidden bg-white transition-all`}>
                                            <iframe
                                                ref={previewFrameRef}
                                                onLoad={wirePreviewFrame}
                                                className="h-full min-h-[68vh] w-full border-none bg-white"
                                                srcDoc={getPreviewHtml(viewMode === 'visual')}
                                                title="Preview"
                                            />
                                        </div>
                                    </div>
                                    {viewMode === 'visual' && (
                                        <div className="max-h-[38vh] w-full overflow-y-auto border-t bg-white p-4 xl:max-h-none xl:w-80 xl:border-l xl:border-t-0">
                                            {!selectedElement ? (
                                                <div className="flex h-full min-h-40 items-center justify-center rounded-2xl border-2 border-dashed border-gray-100 p-5 text-center text-xs font-black leading-relaxed text-gray-300">
                                                    {copy.noElementSelected}
                                                </div>
                                            ) : selectedElement.type === 'image' ? (
                                                <div className="space-y-4">
                                                    <div>
                                                        <p className="mb-2 text-[10px] font-black uppercase tracking-widest text-gray-400">{copy.selectedImage}</p>
                                                        <div className="overflow-hidden rounded-2xl border border-gray-100 bg-gray-50">
                                                            <img src={selectedElement.src} alt="" className="h-40 w-full object-contain" />
                                                        </div>
                                                    </div>
                                                    <p className="break-all rounded-2xl bg-gray-50 p-3 font-mono text-[10px] text-gray-500">{selectedElement.label}</p>
                                                </div>
                                            ) : (
                                                <div className="space-y-4">
                                                    <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">{copy.selectedText}</p>
                                                    <textarea
                                                        ref={selectedHtmlRef}
                                                        className="h-48 w-full resize-none rounded-2xl border border-gray-100 bg-gray-50 p-4 font-mono text-xs leading-relaxed outline-none focus:ring-4 focus:ring-blue-50"
                                                        value={selectedHtml}
                                                        onChange={(event) => setSelectedHtml(event.target.value)}
                                                    />
                                                    <div className="grid grid-cols-3 gap-2">
                                                        <button onClick={() => insertBreak('<br>')} className="rounded-xl bg-gray-100 px-2 py-3 text-[9px] font-black text-gray-500">{copy.commonBreak}</button>
                                                        <button onClick={() => insertBreak('<br class="sb-break-desktop">')} className="rounded-xl bg-blue-50 px-2 py-3 text-[9px] font-black text-blue-600">{copy.desktopBreak}</button>
                                                        <button onClick={() => insertBreak('<br class="sb-break-mobile">')} className="rounded-xl bg-indigo-50 px-2 py-3 text-[9px] font-black text-indigo-600">{copy.mobileBreak}</button>
                                                    </div>
                                                    <button onClick={applySelectedText} className="w-full rounded-full bg-blue-600 py-4 text-xs font-black text-white shadow-xl shadow-blue-100 hover:bg-blue-700 active:scale-95">{copy.applyText}</button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="px-4 py-4 sm:px-12 sm:py-8 border-t flex flex-col-reverse sm:flex-row justify-end gap-3 sm:gap-6 bg-white z-10 shadow-[0_-20px_60px_rgba(0,0,0,0.04)]">
                    <button onClick={onClose} className="w-full sm:w-auto py-3 text-xs font-black text-gray-400 hover:text-gray-900 transition-colors tracking-widest uppercase">{copy.discard}</button>
                    <button onClick={() => onSave({ ...formData, html_content: ensureLpHelperStyles(formData.html_content), css_content: '', js_content: '' })} className="w-full sm:w-auto px-6 sm:px-16 py-4 bg-blue-600 text-white rounded-full font-black text-sm shadow-2xl shadow-blue-200 hover:bg-blue-700 hover:scale-105 active:scale-95 transition-all">
                        {copy.save}
                    </button>
                </div>
            </div>
        </div>
    );
}

// --- Main Page ---
export default function MarketingLpPage() {
    const { user } = useOutletContext(); // userを取得
    const isEnglishBeta = isEnglishOperatorMode();
    const copy = getLpCopy(isEnglishBeta);
    const aiCopy = copy.aiMenu;
    
    // 🚀 修正: OEM社長(admin)がプルダウンで選択したクライアントを反映
    const clientId = useMemo(() => (
      user?.role === "admin" || user?.role === "admin_staff" 
        ? localStorage.getItem("selectedClientId") || "" 
        : user?.client_id
    ), [user]);

    const [state, setState] = useState({
        lps: [],
        forms: [],
        isLoading: true,
        editingLp: null,
        showPromptModal: false
    });

    const [isAiMenuOpen, setIsAiMenuOpen] = useState(false);

    const fetchData = useCallback(async () => {
        if (!clientId) return;
        setState(s => ({ ...s, isLoading: true }));
        try {
            const [lpRes, formRes] = await Promise.all([
                fetch(`${API_URL}/api/marketing/lps?client_id=${clientId}`, { headers: getAuthHeader() }),
                fetch(`${API_URL}/api/marketing/forms?client_id=${clientId}`, { headers: getAuthHeader() })
            ]);
            const [lpData, formData] = await Promise.all([lpRes.json(), formRes.json()]);
            // ★ プラン状況を確認するAPIを叩く
            const usageRes = await fetch(`${API_URL}/api/usage-status?clientId=${clientId}`, { headers: getAuthHeader() });
            const usageData = await usageRes.json();

            setState(s => ({
                ...s,
                isLoading: false,
                lps: lpData.success ? lpData.lps : [],
                forms: formData.success ? formData.forms : [],
                isFreePlan: usageData.plan === 'free' // ★ ここでプラン情報をセット
            }));
        } catch (e) { console.error(e); setState(s => ({ ...s, isLoading: false })); }
    }, [clientId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleSave = async (formData) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/lps`, {
                method: "POST",
                headers: { ...getAuthHeader(true) },
                body: JSON.stringify({ ...formData, client_id: clientId })
            });
            const data = await res.json();
            if (data.success) {
                setState(s => ({ ...s, editingLp: null }));
                fetchData();
            } else { throw new Error(data.error); }
        } catch (err) { alert(err.message); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm(copy.deleteConfirm)) return;
        try {
            await fetch(`${API_URL}/api/marketing/lps/${id}`, {
                method: "DELETE",
                headers: { ...getAuthHeader(true) },
                body: JSON.stringify({ client_id: clientId })
            });
            fetchData();
        } catch (e) { alert(copy.deleteFailed); }
    };

    const handleEditClick = async (sum) => {
        try {
            const res = await fetch(`${API_URL}/api/marketing/lps/${sum.id}?client_id=${clientId}`, { headers: getAuthHeader() });
            const data = await res.json();
            if (data.success) setState(s => ({ ...s, editingLp: data.lp }));
        } catch (e) { alert(copy.dataError); }
    };

    return (
        <div className="p-3 sm:p-6 lg:p-12 bg-gray-50/30 min-h-full font-sans selection:bg-blue-100">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 sm:gap-8 mb-10 sm:mb-20">
                    <div>
                        <h1 className="text-2xl sm:text-5xl font-black text-gray-900 tracking-tight">{copy.pageTitle}</h1>
                        <p className="text-xs sm:text-sm text-gray-400 mt-3 sm:mt-4 font-bold uppercase tracking-[0.18em] sm:tracking-[0.3em]">{copy.pageSubtitle}</p>
                    </div>
                    <div className="flex w-full gap-3 sm:w-auto sm:gap-4">
                        {!isEnglishBeta && (
                            <div className="relative">
                                {/* メインボタン：クリックでメニューをトグル */}
                                <button 
                                    onClick={() => setIsAiMenuOpen(!isAiMenuOpen)}
                                    className={`p-4 sm:p-5 rounded-full shadow-lg transition-all hover:-translate-y-1 active:scale-95 ${
                                        isAiMenuOpen ? 'bg-indigo-600 text-white' : 'bg-white border-2 border-gray-100 text-gray-900'
                                    }`}
                                >
                                    <Icons.Magic />
                                </button>
                                
                                {/* AIメニューリスト（クリックで表示） */}
                                {isAiMenuOpen && (
                                    <>
                                        {/* 背景をクリックしたら閉じるためのオーバーレイ */}
                                        <div className="fixed inset-0 z-[90]" onClick={() => setIsAiMenuOpen(false)} />
                                        
                                        <div className="absolute right-0 mt-4 w-[calc(100vw-2rem)] max-w-72 bg-white rounded-[2rem] shadow-[0_20px_50px_rgba(0,0,0,0.15)] border border-gray-50 z-[100] p-4 animate-in zoom-in-95 duration-200">
                                            <p className="text-[10px] font-black text-gray-400 uppercase px-4 py-2 tracking-widest">{aiCopy.mainAction}</p>
                                            
                                            {/* ★ 一番上にプロンプト生成を配置 */}
                                            <button 
                                                onClick={() => {
                                                    setState(s => ({...s, showPromptModal: true}));
                                                    setIsAiMenuOpen(false);
                                                }}
                                                className="w-full flex items-center gap-4 p-4 bg-indigo-50 text-indigo-700 rounded-2xl hover:bg-indigo-600 hover:text-white transition-all group"
                                            >
                                                <div className="bg-white/50 p-2 rounded-lg group-hover:bg-indigo-400">
                                                    <Icons.Magic />
                                                </div>
                                                <div className="text-left">
                                                    <p className="text-xs font-black">{aiCopy.generatePrompt}</p>
                                                    <p className="text-[9px] opacity-70 font-bold uppercase">{aiCopy.generatePromptSub}</p>
                                                </div>
                                            </button>

                                            <div className="h-px bg-gray-100 my-3 mx-2" />

                                            <p className="text-[10px] font-black text-gray-400 uppercase px-4 py-2 tracking-widest">{aiCopy.production}</p>
                                            <a href="https://gemini.google.com/gem/1l3jQkeEtG8CXvOl16PyJO2lOS9o_Npr2?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-3 hover:bg-blue-50 rounded-2xl transition-colors">
                                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 text-[10px] font-black">G</div>
                                                <span className="text-xs font-bold text-gray-700">{aiCopy.geminiProduction}</span>
                                            </a>
                                            <a href="https://chatgpt.com/g/g-69817bf04fb48191b82c9a12044bbfe9-sales-breaker-lpzhi-zuo-asisutanto" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-3 hover:bg-green-50 rounded-2xl transition-colors">
                                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-600 text-[10px] font-black">C</div>
                                                <span className="text-xs font-bold text-gray-700">{aiCopy.chatgptProduction}</span>
                                            </a>
                                            
                                            <div className="h-px bg-gray-100 my-3 mx-2" />
                                            
                                            <p className="text-[10px] font-black text-gray-400 uppercase px-4 py-2 tracking-widest">{aiCopy.inspection}</p>
                                            <a href="https://gemini.google.com/gem/1o4-FWBJ0aSgtYtBWygdU1CKfbunx0GcD?usp=sharing" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-3 hover:bg-orange-50 rounded-2xl transition-colors">
                                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600"><Icons.Check /></div>
                                                <span className="text-xs font-bold text-gray-700">{aiCopy.geminiInspection}</span>
                                            </a>
                                            <a href="https://chatgpt.com/g/g-69817c7c818081919958ad364c75bba9-sales-breaker-lpshi-yang-jian-pin-zui-zhong-tietuku" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 p-3 hover:bg-orange-50 rounded-2xl transition-colors">
                                                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center text-orange-600"><Icons.Check /></div>
                                                <span className="text-xs font-bold text-gray-700">{aiCopy.chatgptInspection}</span>
                                            </a>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        <button 
                            onClick={() => {
                                // ★ 無料プラン制限チェックを復活
                                if (state.isFreePlan && state.lps.length >= 1) {
                                    alert(copy.freeLimit);
                                    return;
                                }
                                setState(s => ({...s, editingLp: {}}));
                            }} 
                            className="flex flex-1 items-center justify-center gap-3 rounded-full bg-blue-600 px-6 py-4 text-sm font-black text-white shadow-[0_25px_60px_-15px_rgba(37,99,235,0.4)] transition-all hover:-translate-y-1 hover:bg-blue-700 active:scale-95 sm:flex-none sm:px-10"
                        >
                            <Icons.Plus /> <span>{copy.createButton}</span>
                        </button>
                    </div>
                </div>

                {state.isLoading ? (
                    <div className="bg-white rounded-3xl sm:rounded-[3.5rem] p-8 sm:p-20 lg:p-32 shadow-sm border border-gray-50 flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-4 border-blue-600 mb-8 sm:mb-10"></div>
                        <p className="text-gray-300 font-black tracking-[0.4em] uppercase text-[10px]">{copy.loading}</p>
                    </div>
                ) : state.lps.length === 0 ? (
                    <div className="bg-white rounded-3xl sm:rounded-[3.5rem] p-6 sm:p-20 lg:p-32 shadow-sm border-4 border-dashed border-gray-50 flex flex-col items-center text-center">
                        <div className="bg-gray-50 p-6 sm:p-10 rounded-full mb-6 sm:mb-10 text-gray-200"><Icons.Plus /></div>
                        <h2 className="text-xl sm:text-4xl font-black text-gray-900 mb-4 sm:mb-6">{copy.emptyTitle}</h2>
                        <p className="text-sm sm:text-base text-gray-400 max-w-sm mb-10 sm:mb-14 leading-relaxed font-medium">{copy.emptyBody}</p>
                        <button onClick={() => setState(s => ({...s, editingLp: {}}))} className="text-blue-600 font-black hover:underline tracking-widest uppercase text-xs">{copy.emptyCta}</button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-12">
                        {state.lps.map(lp => (
                            <div key={lp.id} className="bg-white rounded-3xl sm:rounded-[3rem] shadow-sm border border-gray-50/50 hover:shadow-[0_50px_100px_-20px_rgba(0,0,0,0.08)] hover:-translate-y-4 transition-all duration-700 group overflow-hidden">
                                <div className="p-6 sm:p-12">
                                    <div className="flex justify-between items-start mb-10">
                                        <span className={`px-6 py-2 rounded-full text-[9px] font-black uppercase tracking-widest shadow-sm ${lp.is_active ? 'bg-green-50 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                                            {lp.is_active ? 'Online' : 'Draft'}
                                        </span>
                                        <div className="flex gap-2 opacity-100 transition-all duration-500 sm:opacity-0 sm:group-hover:opacity-100 sm:translate-x-4 sm:group-hover:translate-x-0">
                                            <button onClick={() => handleEditClick(lp)} className="p-3.5 text-gray-300 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-all"><Icons.Edit /></button>
                                            <button onClick={() => handleDelete(lp.id)} className="p-3.5 text-gray-300 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"><Icons.Trash /></button>
                                        </div>
                                    </div>
                                    <h3 className="text-xl sm:text-2xl font-black text-gray-900 mb-2 break-words group-hover:text-blue-600 transition-colors">{lp.title}</h3>
                                    <div className="flex flex-wrap items-center gap-3 mb-8 sm:mb-12">
                                        <p className="text-[10px] text-gray-400 font-mono bg-gray-50 px-2 py-1 rounded-lg">/p/{lp.slug}</p>
                                        <div className="h-1 w-1 bg-gray-200 rounded-full"></div>
                                        <p className="text-[10px] text-gray-400 font-black uppercase tracking-widest">{lp.view_count || 0} {copy.views}</p>
                                    </div>
                                    
                                    <div className="flex items-center gap-3 sm:gap-4 pt-8 sm:pt-10 border-t border-gray-50">
                                        <button onClick={() => { navigator.clipboard.writeText(`${PUBLISH_DOMAIN}/p/${lp.slug}`); alert(copy.copyUrl); }} className="flex-grow flex items-center justify-center gap-3 bg-gray-900 text-white text-[10px] font-black py-5 rounded-2xl hover:bg-indigo-600 shadow-2xl shadow-gray-100 transition-all uppercase tracking-[0.2em]">
                                            <Icons.Copy /> Copy Link
                                        </button>
                                        {lp.is_active && (
                                            <a href={`${PUBLISH_DOMAIN}/p/${lp.slug}`} target="_blank" rel="noopener noreferrer" className="p-5 bg-gray-50 text-gray-300 rounded-2xl hover:bg-blue-50 hover:text-blue-500 transition-all border border-transparent hover:border-blue-100">
                                                <Icons.External />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            {state.editingLp && (
                <LpEditorModal 
                    lp={state.editingLp.id ? state.editingLp : null} 
                    forms={state.forms} 
                    onClose={() => setState(s => ({...s, editingLp: null}))} 
                    onSave={handleSave} 
                    clientId={clientId} 
                    isEnglishBeta={isEnglishBeta}
                />
            )}
            {state.showPromptModal && !isEnglishBeta && <LpPromptGeneratorModal forms={state.forms} onClose={() => setState(s => ({...s, showPromptModal: false}))} isEnglishBeta={isEnglishBeta} />}
        </div>
    );
}
